import { Status } from "./status";

export interface Organization {
  id: string;
  name: string;
  type: string;
  email: string;
  phoneNo: string;
  noDocument: number;
  status: Status;
}

export type OrganizationContactPerson = {
  email?: string;
  fullName?: string;
  phoneNumber?: string;
  phone?: string;
  username?: string;
};

export type OrganizationDocument = {
  id?: number;
  name?: string;
  documentType?: string;
  file?: string;
};

export type OrganizationBankAccount = {
  accountBranch?: string;
  accountName?: string;
  accountNumber?: string;
  bankId?: number;
  bankName?: string;
  id?: number;
  isActive?: boolean;
  isPrimary?: boolean;
};

export type OrganizationDetailPayload = {
  id?: number | string;
  address?: string | Record<string, unknown>;
  bankAccounts?: OrganizationBankAccount[];
  contactPerson?: OrganizationContactPerson;
  documents?: OrganizationDocument[];
  email?: string;
  name?: string;
  phoneNumber?: string;
  phoneNo?: string;
  shortName?: string;
  type?: string;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  user?: Array<{
    fullName?: string;
    email?: string;
    phone?: string;
    phoneNumber?: string;
    username?: string;
  }>;
};

export function organizationDetailFromResponse(
  response: { data?: unknown } | undefined,
): OrganizationDetailPayload | undefined {
  const body = response?.data as { data?: OrganizationDetailPayload } | undefined;
  if (!body) return undefined;
  return body.data ?? (body as unknown as OrganizationDetailPayload);
}

export function formatOrganizationAddress(
  raw: string | Record<string, unknown> | undefined,
): string {
  if (!raw) return "Not specified";

  if (typeof raw === "object") {
    const parts = [
      raw.region,
      raw.city,
      raw.kifleKetema ?? raw.kifle_ketema ?? raw.subCity,
      raw.woreda,
      raw.kebele,
      raw.house_number ?? raw.houseNumber,
    ].filter(Boolean);
    return parts.length ? parts.map(String).join(", ") : "Not specified";
  }

  if (!String(raw).trim()) return "Not specified";
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed && typeof parsed === "object") {
      return formatOrganizationAddress(parsed);
    }
  } catch {
    /* plain string */
  }
  return raw;
}

export type OrganizationAddressFields = {
  region: string;
  city: string;
  kifleKetema: string;
  woreda: string;
  kebele: string;
  houseNumber: string;
};

export function organizationBankIdsFromAccounts(
  accounts?: OrganizationBankAccount[],
): number[] {
  if (!accounts?.length) return [];
  const ids = accounts
    .map((a) => a.bankId)
    .filter((id): id is number => id != null && !Number.isNaN(Number(id)));
  return Array.from(new Set(ids.map(Number)));
}

export type OrganizationBankAccountInput = {
  id?: number;
  bankId: number;
  accountName?: string;
  accountNumber?: string;
  accountBranch?: string;
  isPrimary?: boolean;
  isActive?: boolean;
};

export type OrganizationUpdatePayload = {
  name: string;
  shortName?: string;
  type?: string;
  email?: string;
  phoneNumber?: string;
  address: string;
  contactPerson?: OrganizationContactPerson;
  bankAccounts: OrganizationBankAccountInput[];
};

export function resolveOrganizationContactPerson(
  organization?: OrganizationDetailPayload,
): OrganizationContactPerson {
  const cp = organization?.contactPerson;
  const primaryUser = organization?.user?.[0];

  return {
    fullName: cp?.fullName ?? primaryUser?.fullName ?? "",
    email: cp?.email ?? primaryUser?.email ?? "",
    phoneNumber:
      cp?.phoneNumber ??
      cp?.phone ??
      primaryUser?.phoneNumber ??
      primaryUser?.phone ??
      "",
    username: cp?.username ?? primaryUser?.username ?? "",
  };
}

function addressFromRecord(
  parsed: Record<string, unknown>,
): OrganizationAddressFields {
  return {
    region: String(parsed.region ?? ""),
    city: String(parsed.city ?? ""),
    kifleKetema: String(
      parsed.kifleKetema ?? parsed.kifle_ketema ?? parsed.subCity ?? "",
    ),
    woreda: String(parsed.woreda ?? ""),
    kebele: String(parsed.kebele ?? ""),
    houseNumber: String(parsed.house_number ?? parsed.houseNumber ?? ""),
  };
}

export function parseOrganizationAddress(
  raw?: string | Record<string, unknown>,
): OrganizationAddressFields {
  const empty: OrganizationAddressFields = {
    region: "",
    city: "",
    kifleKetema: "",
    woreda: "",
    kebele: "",
    houseNumber: "",
  };

  if (!raw) return empty;

  if (typeof raw === "object") {
    return addressFromRecord(raw);
  }

  if (!String(raw).trim()) return empty;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed && typeof parsed === "object") {
      return addressFromRecord(parsed);
    }
  } catch {
    /* plain string */
  }
  return empty;
}
